import { CurrencyDollarIcon } from "@phosphor-icons/react/dist/csr/CurrencyDollar";
import { Formik, Form } from "formik";
import { useState } from "react";
import * as Yup from "yup";
import { Button } from "@volca/ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@volca/ui/components/ui/dialog";
import { MoneyInput } from "@volca/ui/components/ui/money-input";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { toast } from "@volca/ui/lib/toast";
import { NewBankDetailsModal } from "@/business-settings/payments/new-bank-details-modal";
import { parseMaskedDollarInput } from "@/core/utils/money";
import {
  useDepositWithdraw,
  useGetAchAccount,
  useGetAccountSummary,
  useGetInternalTransactions,
} from "@/internal-transactions/query";

const schema = Yup.object().shape({
  amount: Yup.number()
    .transform((_, orig) => {
      if (orig === "" || orig === null || orig === undefined) return undefined;
      const parsed = parseMaskedDollarInput(orig);
      return parsed !== null ? parsed : undefined;
    })
    .required("Amount is required")
    .positive("Amount must be positive")
    .min(1, "Amount must be at least 1"),
});

interface PaymentModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete: () => void;
  businessId?: string;
}

const PaymentModal = ({
  isOpen,
  onOpenChange,
  onComplete,
  businessId,
}: PaymentModalProps) => {
  const [isSuccess, setIsSuccess] = useState(false);
  const [showBankSetup, setShowBankSetup] = useState(false);
  const {
    data: achAccount,
    isLoading: achLoading,
    isFetched: achFetched,
  } = useGetAchAccount(businessId);
  const {
    data: accountSummary,
    isLoading: summaryLoading,
    isFetched: summaryFetched,
  } = useGetAccountSummary(businessId);
  const {
    data: internalTransactions = [],
    isLoading: txLoading,
    isFetched: txFetched,
  } = useGetInternalTransactions(businessId);
  const { mutate: deposit, isPending } = useDepositWithdraw(true, businessId);

  const hasAchAccount = !!achAccount;
  const hasBalance = Number(accountSummary?.wallet_balance ?? 0) > 0;
  const hasTransactions = internalTransactions.length > 0;
  const shouldShowDeposit = hasAchAccount && !hasBalance && !hasTransactions;
  const isLoading = achLoading || summaryLoading || txLoading;
  const isResolved = achFetched && summaryFetched && txFetched;

  const handleClose = () => {
    if (!isPending) {
      setIsSuccess(false);
      setShowBankSetup(false);
      onOpenChange(false);
    }
  };

  const handleComplete = () => {
    setIsSuccess(false);
    onComplete();
  };

  const handleAddBankAccount = () => {
    setShowBankSetup(true);
  };

  if (showBankSetup) {
    return (
      <NewBankDetailsModal
        isOpen={showBankSetup}
        onOpenChange={setShowBankSetup}
        businessId={businessId}
      />
    );
  }

  if (isLoading || !isResolved) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Checking account status…</DialogTitle>
          </DialogHeader>
          <Stack className="w-full" align="start" gap={6}>
            <div className="flex items-center justify-center py-6">
              <Spinner aria-hidden size={24} className="text-subtle" />
            </div>
          </Stack>
        </DialogContent>
      </Dialog>
    );
  }

  if (!hasAchAccount) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Bank account required</DialogTitle>
          </DialogHeader>

          <Stack className="w-full" align="start" gap={6}>
            <Stack gap={4} align="center" className="w-full">
              <CurrencyDollarIcon className="h-12 w-12 text-subtle" />
              <Text className="text-center text-subtle">
                You need to add your bank account details before you can deposit
                funds.
              </Text>
            </Stack>

            <DialogFooter className="self-stretch">
              <Button variant="secondary" onClick={handleClose}>
                Cancel
              </Button>
              <Button onClick={handleAddBankAccount}>Add bank account</Button>
            </DialogFooter>
          </Stack>
        </DialogContent>
      </Dialog>
    );
  }

  if (!shouldShowDeposit) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>No action required</DialogTitle>
          </DialogHeader>

          <Stack className="w-full" align="start" gap={6}>
            <Stack gap={4} align="center" className="w-full">
              <CurrencyDollarIcon className="h-12 w-12 text-subtle" />
              <Text className="text-center text-subtle">
                {hasBalance
                  ? "You already have funds in your account."
                  : "You have existing transaction history."}
              </Text>
            </Stack>

            <DialogFooter className="self-stretch sm:justify-center">
              <Button onClick={handleComplete}>Continue</Button>
            </DialogFooter>
          </Stack>
        </DialogContent>
      </Dialog>
    );
  }

  if (isSuccess) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Deposit successful</DialogTitle>
          </DialogHeader>

          <Stack className="w-full" align="start" gap={6}>
            <Stack gap={4} align="center" className="w-full">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-success-subtle">
                <CurrencyDollarIcon className="h-6 w-6 text-success" />
              </div>
              <Text className="text-center text-subtle">
                Your deposit has been initiated successfully. It may take a few
                business days to appear in your account.
              </Text>
            </Stack>

            <DialogFooter className="self-stretch sm:justify-center">
              <Button onClick={handleComplete}>Continue</Button>
            </DialogFooter>
          </Stack>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Deposit funds</DialogTitle>
        </DialogHeader>

        <Formik
          initialValues={{
            amount: "",
          }}
          validationSchema={schema}
          validateOnMount={true}
          onSubmit={(values, { setSubmitting }) => {
            const parsed = parseMaskedDollarInput(String(values.amount ?? ""));
            const amountCents = parsed !== null ? Math.round(parsed * 100) : 0;

            return deposit(
              { amount: amountCents },
              {
                onSuccess: () => {
                  toast.success("Deposit successfully initiated");
                  setSubmitting(false);
                  setIsSuccess(true);
                },
                onError: () => {
                  toast.error("Failed to initiate deposit. Please try again.");
                  setSubmitting(false);
                },
              },
            );
          }}
        >
          {({
            isSubmitting,
            isValid,
            errors,
            touched,
            values,
            setFieldValue,
          }) => (
            <Form>
              <Stack className="w-full" align="start" gap={6}>
                <Stack align="start" gap={4} className="w-full">
                  <Text className="text-subtle">
                    Enter the amount you'd like to deposit into your Volca
                    account.
                  </Text>

                  <MoneyInput
                    label="Amount"
                    placeholder="0.00"
                    error={touched.amount ? errors.amount : undefined}
                    value={values.amount}
                    onChange={(value) => setFieldValue("amount", value)}
                    name="amount"
                    fullWidth
                  />

                  {achAccount && (
                    <Text variant="caption" className="text-subtle">
                      Funds will be withdrawn from your {achAccount.name}{" "}
                      account ending in {achAccount.mask}
                    </Text>
                  )}
                </Stack>

                <DialogFooter className="self-stretch">
                  <Button
                    variant="secondary"
                    onClick={handleClose}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={!isValid || isSubmitting}
                    loading={isSubmitting}
                    loadingLabel="Deposit, in progress"
                  >
                    <CurrencyDollarIcon
                      className="h-4 w-4"
                      data-icon="inline-start"
                    />
                    Deposit
                  </Button>
                </DialogFooter>
              </Stack>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentModal;
